import type { IMediaSchema, IMediumAssetSchema } from '@monofe/streamlib';
import type { IProgressPromise, IProgressPromiseGenerator } from '../../interface/promise';
import type { IFetcher } from '../../interface/util';
import type { IMediaURLResolverParameters, MediaBinaryStorageTypeMap } from '../interface/media';
import mediaDownload from './media.download';
import mediaUtil from './media.util';

const MIME_TYPE_STREAM = ['application/x-mpegurl'];

const _isStream = (asset: IMediumAssetSchema) => MIME_TYPE_STREAM.includes(asset.mimeType);
const _isDownload = (asset: IMediumAssetSchema) => !_isStream(asset);

function canResolveMedium(schema: IMediaSchema, mediaId: string): Promise<boolean> {
    /* check if the mediaId lives in our schema */
    const matchingItems = schema.media.filter(medium => medium.sourceGuid === mediaId);

    return Promise.resolve(matchingItems.length > 0);
}
function requestURL(
    schema: IMediaSchema,
    urlPrefix: string,
    mediaId: string,
    parameters: IMediaURLResolverParameters = {},
): Array<string> {
    const matchingItems = schema.media.filter(medium => medium.sourceGuid === mediaId);

    /* prefer stream assets where possible */
    const streamVersions = matchingItems.flatMap(asset =>
        asset.versions.filter(version => _isStream(version)),
    );
    const downloadVersions = matchingItems.flatMap(asset =>
        asset.versions.filter(version => _isDownload(version)),
    );

    const matchingVersion = streamVersions.length > 0 ? streamVersions : downloadVersions;

    const urls = matchingVersion.map(version => `${urlPrefix}${version.path}`);

    return urls;
}

function getMedia<
    K extends keyof MediaBinaryStorageTypeMap,
    V extends MediaBinaryStorageTypeMap[K],
>(
    promiser: IProgressPromiseGenerator,
    fetcher: Promise<IFetcher>,
    schema: IMediaSchema,
    urlPrefix: string,
    type: K,
    mediaId: string,
    parameters: IMediaURLResolverParameters,
): IProgressPromise<V> {
    const urls = requestURL(schema, urlPrefix, mediaId, parameters);
    const requests = urls.map(url => mediaUtil.requestForURL(url, type));

    return mediaDownload.download(promiser, fetcher, requests, type);
}

const module = {
    canResolveMedium,
    getMedia,
    requestURL,
};

export default module;
